import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Backend media paths ("/api/media/...") must be absolute inside the native app
export const mediaSrc = (u) => (typeof u === "string" && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer token from localStorage as a fallback for environments where
// cookies aren't reliably sent (iOS Capacitor WKWebView, some third-party contexts).
api.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem("kh_token");
    if (t && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  } catch { /* ignore */ }
  return config;
});

// Global 401 handler + data-mutation event. The event is fired on ANY
// successful non-GET admin request so widgets can self-refresh without
// each one wiring an onChange callback. Listen with:
//   window.addEventListener("kh:data-mutated", () => reload())
// Track consecutive 401s so a single stray/transient failure doesn't kick
// a logged-in user out. Only redirect to /login when we see at least two
// 401s in a short window (which really means the token is bad).
let _authFailCount = 0;
let _lastAuthFailAt = 0;

api.interceptors.response.use(
  (response) => {
    try {
      const method = String(response.config?.method || "get").toLowerCase();
      if (method !== "get") {
        const url = String(response.config?.url || "");
        // Skip extremely chatty endpoints so we don't thrash the UI
        const skip = ["/push/native-token", "/location/update", "/presence/", "/notifications/read-all", "/notifications/", "/support/chat/read"];
        if (!skip.some(s => url.includes(s))) {
          window.dispatchEvent(new CustomEvent("kh:data-mutated", { detail: { url, method } }));
        }
      }
    } catch { /* ignore */ }
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      const now = Date.now();
      if (now - _lastAuthFailAt > 10_000) _authFailCount = 0;
      _authFailCount += 1;
      _lastAuthFailAt = now;
      const requestUrl = String(error?.config?.url || "");
      const isAuthProbe = requestUrl.includes("/auth/me");
      if (!isAuthProbe && _authFailCount >= 2) {
        try { localStorage.removeItem("kh_token"); } catch { /* ignore */ }
        const path = window.location.pathname;
        const publicPaths = ["/login", "/register", "/forgot-password", "/privacy", "/terms", "/data-deletion", "/auth-callback"];
        const onPublic = publicPaths.some((p) => path.startsWith(p)) || path === "/";
        if (!onPublic) {
          sessionStorage.setItem("post_login_redirect", path);
          window.location.assign("/login");
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "حدث خطأ. الرجاء المحاولة مرة أخرى.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatIQD(amount) {
  const n = Math.round(Number(amount) || 0);
  return n.toLocaleString("ar-IQ") + " د.ع";
}

// Extracts lat,lng from a Google Maps URL or "lat,lng" text. Returns null if not found.
export function extractCoords(text) {
  if (!text) return null;
  const s = String(text);
  // Try ?q=lat,lng or @lat,lng or /maps/place/.../@lat,lng/...
  const re = /(-?\d{1,3}\.\d+)[,\s]+(-?\d{1,3}\.\d+)/;
  const m = s.match(re);
  if (m) {
    return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  }
  return null;
}

export function googleMapsUrl(text) {
  if (!text) return null;
  const s = String(text).trim();
  if (s.startsWith("http")) return s;
  const coords = extractCoords(s);
  if (coords) return `https://www.google.com/maps?q=${coords.lat},${coords.lng}`;
  return `https://www.google.com/maps?q=${encodeURIComponent(s)}`;
}

export function wazeUrl(text) {
  if (!text) return null;
  const s = String(text).trim();
  const coords = extractCoords(s);
  if (coords) return `https://www.waze.com/ul?ll=${coords.lat}%2C${coords.lng}&navigate=yes`;
  return `https://www.waze.com/ul?q=${encodeURIComponent(s)}&navigate=yes`;
}

export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

// Downscale any image file (JPEG/PNG/WEBP/HEIC where the browser supports it)
// to a reasonable size BEFORE upload. Keeps huge phone photos from being
// rejected by the 6 MB limit and from locking up the UI on save.
export async function compressImageToDataURL(file, maxDim = 1400, quality = 0.85) {
  // Browsers that can't decode the format (HEIC on Android) → send raw bytes;
  // the backend uses pillow-heif as a fallback.
  const canCanvas = /^image\/(jpe?g|png|webp|gif|bmp)$/i.test(file.type);
  if (!canCanvas) return readFileAsDataURL(file);
  const bitmap = await (window.createImageBitmap
    ? createImageBitmap(file).catch(() => null)
    : null);
  const src = bitmap || await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img); img.onerror = rej;
    img.src = URL.createObjectURL(file);
  });
  const w = src.width || src.naturalWidth;
  const h = src.height || src.naturalHeight;
  if (!w || !h) return readFileAsDataURL(file);
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const cw = Math.round(w * scale), ch = Math.round(h * scale);
  const canvas = document.createElement("canvas");
  canvas.width = cw; canvas.height = ch;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(src, 0, 0, cw, ch);
  return canvas.toDataURL("image/jpeg", quality);
}
